import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Avatar,
    TextField,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Pagination,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogActions,
    Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';

// Define styled components
const CoachesContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    backgroundColor: '#1c1c1c',
    minHeight: '100vh',
});

const HeaderContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    '@media (max-width: 768px)': {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
});

const Title = styled(Typography)({
    fontSize: '1.8rem',
    color: '#fff',
    fontWeight: 'bold',
    '@media (max-width: 768px)': {
        fontSize: '1.5rem',
    },
});

const SearchBar = styled(TextField)({
    marginBottom: '20px',
    width: '100%',
    backgroundColor: '#202020',
    borderRadius: '8px',
    color: '#fff',
    '& .MuiInputBase-root': {
        color: '#fff',
        fontSize: '0.9rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#12c099',
    },
    '& .MuiInputLabel-root': {
        color: '#12c099',
        fontSize: '0.9rem',
    },
});

const StyledTableContainer = styled(TableContainer)({
    backgroundColor: '#202020',
    borderRadius: '12px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
    overflowX: 'auto',
    '@media (max-width: 768px)': {
        width: '100%',
    },
});

const CompactTableCell = styled(TableCell)({
    // ✅ COMPACTION: Reduced padding and font size for a tighter look.
    padding: '10px 16px',
    fontSize: '0.9rem',
    '@media (max-width: 768px)': {
        display: 'block',
        width: '100%',
        padding: '12px 0',
        fontSize: '0.9rem',
        textAlign: 'left',
    },
    color: '#fff',
    whiteSpace: 'nowrap', // Prevents text from wrapping
});

const CompactTableRow = styled(TableRow)({
    '@media (max-width: 768px)': {
        display: 'block',
        width: '100%',
        marginBottom: '20px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
        borderRadius: '8px',
        backgroundColor: '#202020',
        overflow: 'hidden',
    },
});

const PaginationBox = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
    '@media (max-width: 768px)': {
        marginTop: '10px',
    },
});

const Footer = styled(Box)({
    padding: '1rem',
    textAlign: 'center',
    color: '#fff',
    backgroundColor: 'transparent',
    marginTop: 'auto',
    width: '100%',
});

const SiteManagers = () => {
    const [siteManagers, setSiteManagers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const rowsPerPage = 25;
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedManagerUid, setSelectedManagerUid] = useState(null);

    useEffect(() => {
        // Fetch site managers data from the backend
        const fetchSiteManagers = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/site-managers`);
                setSiteManagers(response.data);
            } catch (error) {
                console.error('Error fetching site managers data:', error);
            }
        };
        fetchSiteManagers();
    }, []);

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const openDeleteDialogHandler = (firebaseUid) => {
        setSelectedManagerUid(firebaseUid);
        setOpenDeleteDialog(true);
    };

    const closeDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedManagerUid(null);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/delete/site-manager/${selectedManagerUid}`);
            setSiteManagers(siteManagers.filter((manager) => manager.firebaseUid !== selectedManagerUid));
            closeDeleteDialog();
        } catch (error) {
            console.error('Error deleting site manager:', error);
        }
    };

    const filteredManagers = siteManagers.filter(manager =>
        (manager.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedManagers = filteredManagers.sort((a, b) => {
        const aValue = a[orderBy] || '';
        const bValue = b[orderBy] || '';
        if (order === 'asc') {
            return aValue.localeCompare(bValue);
        }
        return bValue.localeCompare(aValue);
    });

    const paginatedManagers = sortedManagers.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    return (
        <CoachesContainer> {/* Renamed from SiteManagersContainer for consistency, can be reverted if needed */}
            <HeaderContainer>
                <Title variant="h4" gutterBottom>Site staff</Title>
                <SearchBar
                    variant="outlined"
                    label="Search Site staff"
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </HeaderContainer>
            <StyledTableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <CompactTableCell>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={() => handleSortRequest('name')}
                                    sx={{ '& .MuiTableSortLabel-icon': { color: '#12c099 !important' } }}
                                >
                                    Name
                                </TableSortLabel>
                            </CompactTableCell>
                            <CompactTableCell>
                                <TableSortLabel
                                    active={orderBy === 'city'}
                                    direction={orderBy === 'city' ? order : 'asc'}
                                    onClick={() => handleSortRequest('city')}
                                    sx={{ '& .MuiTableSortLabel-icon': { color: '#12c099 !important' } }}
                                >
                                    Location
                                </TableSortLabel>
                            </CompactTableCell>
                            <CompactTableCell>
                                <TableSortLabel
                                    active={orderBy === 'rating'}
                                    direction={orderBy === 'rating' ? order : 'asc'}
                                    onClick={() => handleSortRequest('rating')}
                                    sx={{ '& .MuiTableSortLabel-icon': { color: '#12c099 !important' } }}
                                >
                                    Rating
                                </TableSortLabel>
                            </CompactTableCell>
                            <CompactTableCell>Actions</CompactTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedManagers.map((manager) => (
                            <CompactTableRow key={manager._id}>
                                <CompactTableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar
                                            // ✅ COMPACTION: Made avatar smaller
                                            sx={{ marginRight: '10px', width: 28, height: 28, fontSize: '0.85rem' }}
                                        >
                                            {(manager.name || ' ').charAt(0)}
                                        </Avatar>
                                        <Link
                                            to={`/site-manager/${manager.firebaseUid}`}
                                            // ✅ COMPACTION: Reduced link font size
                                            style={{ color: '#12c099', textDecoration: 'none', fontSize: '0.9rem' }}
                                        >
                                            {manager.name}
                                        </Link>
                                    </Box>
                                </CompactTableCell>
                                <CompactTableCell>{`${manager.city || 'N/A'}, ${manager.state || 'N/A'}`}</CompactTableCell>
                                <CompactTableCell>{manager.rating || 'N/A'}</CompactTableCell>
                                <CompactTableCell>
                                    <IconButton onClick={() => openDeleteDialogHandler(manager.firebaseUid)} sx={{ color: '#ff1744' }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </CompactTableCell>
                            </CompactTableRow>
                        ))}
                    </TableBody>
                </Table>
            </StyledTableContainer>
            <PaginationBox>
                <Pagination
                    count={Math.ceil(filteredManagers.length / rowsPerPage)}
                    page={page}
                    onChange={(event, value) => setPage(value)}
                    sx={{ button: { color: '#12c099' }, ul: { color: '#12c099' } }}
                />
            </PaginationBox>
            <Dialog
                open={openDeleteDialog}
                onClose={closeDeleteDialog}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">
                    Are you sure you want to delete this site manager?
                </DialogTitle>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="secondary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Footer>
                <Typography variant="body2">
                    © {new Date().getFullYear()} Mars Reel Media Corp. All rights reserved.
                </Typography>
            </Footer>
        </CoachesContainer>
    );
};

export default SiteManagers;